import { Model } from './model';
import { observable, action, computed, runInAction } from 'mobx';
import { DateTime } from 'luxon';
import { Order } from './order';
import { toISODate } from '../datetime';

export class OrderSplitByDate extends Model {
    @observable public childOrders: ChildOrder[] = [];
    @observable public parentOrder: Order;

    constructor(parentOrder: Order) {
        super();
        runInAction(() => {
            this.parentOrder = parentOrder;

            // Initialize with the first group (parent) using the current delivery date
            const firstChildOrder = new ChildOrder();
            if (this.parentOrder.delivery.dateTba) {
                firstChildOrder.setDateTba(true);
            } else {
                firstChildOrder.setDeliveryDate(this.parentOrder.delivery.date || null);
            }

            // Parent order quantities are computed automatically as original - sum(child orders)
            // No pre-population needed here

            this.childOrders.push(firstChildOrder);
        });
    }

    @action
    public addChildOrder() {
        this.childOrders.push(new ChildOrder());
    }

    @action
    public removeChildOrder(index: number) {
        if (index === 0) {
            throw new Error("Cannot remove the parent order");
        }
        this.childOrders.splice(index, 1);
    }

    // How much the parent order retains for a product line (original qty minus what new child orders take)
    public getParentQuantityForProductLine(lineId: number): number {
        const line = this.parentOrder.productLines.find(l => l.id === lineId);
        if (!line) return 0;
        const remaining = line.quantity - this.getTotalAllocatedForProductLine(lineId);
        return Math.round(Math.max(0, remaining) * 1000) / 1000;
    }

    // Max quantity a specific child order (index 1+) can have for a product line
    public getMaxQuantityForChildOrder(childOrderIndex: number, lineId: number): number {
        const thisChildQty = this.childOrders[childOrderIndex].getQuantityForProductLine(lineId);
        return this.getParentQuantityForProductLine(lineId) + thisChildQty;
    }

    @computed
    public get isValid(): boolean {
        // Must have exactly one new child order to perform a split
        if (this.childOrders.length !== 2) {
            return false;
        }

        // All new child orders must have valid dates
        if (!this.childOrders.slice(1).every(g => g.hasValidDate)) {
            return false;
        }

        // All new child orders must have at least one product item
        if (!this.childOrders.slice(1).every(g => g.hasAnyProductItems)) {
            return false;
        }

        // Parent must retain at least one item
        const parentHasItems = this.parentOrder.productLines.some(line =>
            this.getParentQuantityForProductLine(line.id) > 0
        );

        return parentHasItems;
    }

    @computed
    public get validationErrors(): string[] {
        return this.childOrders.reduce<string[]>(
            (all, _, i) => all.concat(this.getValidationErrorsForChildOrder(i)),
            []
        );
    }

    public getValidationErrorsForChildOrder(index: number): string[] {
        const errors: string[] = [];
        const group = this.childOrders[index];

        if (index === 0) {
            // Parent: must retain at least one item
            const parentHasItems = this.parentOrder.productLines.some(line =>
                this.getParentQuantityForProductLine(line.id) > 0
            );
            if (!parentHasItems) {
                errors.push('Parent order must retain at least one product');
            }
        } else {
            if (!group.hasValidDate) {
                errors.push('Must have a delivery date or be marked as TBA');
            }
            if (!group.hasAnyProductItems) {
                errors.push('No products allocated');
            }
        }

        return errors;
    }

    // Total allocated to new child orders (index 1+) for a product line
    public getTotalAllocatedForProductLine(lineId: number): number {
        const total = this.childOrders.slice(1)
            .map(g => g.getQuantityForProductLine(lineId))
            .reduce((sum, qty) => sum + qty, 0);
        return Math.round(total * 1000) / 1000;
    }

    public toApiPayload() {
        return {
            parentOrder: Order.toRequest(this.parentOrder),
            childOrders: this.childOrders.map((childOrder, index) => ({
                deliveryDate: toISODate(childOrder.deliveryDate),
                dateTba: childOrder.dateTba,
                productAllocations: index === 0
                    // Parent quantities are computed: original minus what child orders took
                    ? this.parentOrder.productLines
                        .map(line => ({ lineId: line.id, quantity: this.getParentQuantityForProductLine(line.id) }))
                        .filter(a => a.quantity > 0)
                    : this.parentOrder.productLines
                        .map(line => ({ lineId: line.id, quantity: childOrder.getQuantityForProductLine(line.id) })),
            }))
        };
    }
}

export class ChildOrder {
    @observable public deliveryDate: DateTime | null;
    @observable public dateTba: boolean;
    @observable public productAllocations: Map<number, number> = new Map();

    constructor() {
        runInAction(() => {
            this.deliveryDate = null;
            this.dateTba = false;
        });
    }

    @action
    public setDeliveryDate(date: DateTime | null) {
        this.deliveryDate = date;
        if (date) {
            this.dateTba = false;
        }
    }

    @action
    public setDateTba(isTba: boolean) {
        this.dateTba = isTba;
        if (isTba) {
            this.deliveryDate = null;
        }
    }

    @action
    public setProductQuantity(lineId: number, quantity: number) {
        if (quantity < 0) {
            throw new Error("Quantity cannot be negative");
        }
        this.productAllocations.set(lineId, quantity);
    }

    public getQuantityForProductLine(lineId: number): number {
        return this.productAllocations.get(lineId) || 0;
    }

    @computed
    public get hasValidDate(): boolean {
        return this.dateTba || this.deliveryDate !== null;
    }

    @computed
    public get hasAnyProductItems(): boolean {
        return Array.from(this.productAllocations.values()).some(qty => qty > 0);
    }

    @computed
    public get totalProducts(): number {
        return Array.from(this.productAllocations.values()).reduce((sum, qty) => sum + qty, 0);
    }
}
